/**
 * Delete Project Modal
 *
 * Two-stage confirmation modal for deleting projects
 * Stage 1: Show impact and get initial confirmation
 * Stage 2: Require typing project name to confirm
 */

import { useState } from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import type { Project } from '@/stores/projectStore';

interface DeleteProjectModalProps {
  project: Project;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeleteProjectModal: React.FC<DeleteProjectModalProps> = ({
  project,
  onClose,
  onConfirm
}) => {
  const [stage, setStage] = useState<1 | 2>(1);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const artifactCount = Object.keys(project.artifacts).length;
  const isConfirmTextValid = confirmText === project.name;

  const handleFirstConfirm = () => {
    setStage(2);
  };

  const handleFinalDelete = async () => {
    if (!isConfirmTextValid) return;

    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Failed to delete project:', error);
      setIsDeleting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isConfirmTextValid) {
      handleFinalDelete();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background border border-border rounded-lg shadow-lg max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-semibold">Delete Project</h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={isDeleting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {stage === 1 ? (
            // Stage 1: Initial Warning
            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50 rounded-md p-4">
                <p className="text-sm text-red-900 dark:text-red-100 font-medium">
                  ⚠️ Warning: This action cannot be undone
                </p>
              </div>

              <div>
                <p className="text-sm text-foreground mb-3">
                  You are about to permanently delete:
                </p>
                <div className="bg-muted rounded-md p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Project:</span>
                    <span className="font-medium">{project.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Artifacts:</span>
                    <span className="font-medium">{artifactCount} {artifactCount === 1 ? 'artifact' : 'artifacts'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Created:</span>
                    <span className="font-medium">
                      {new Date(project.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/50 rounded-md p-3">
                <p className="text-xs text-orange-900 dark:text-orange-100">
                  This will delete all project data including:
                </p>
                <ul className="text-xs text-orange-900 dark:text-orange-100 list-disc list-inside mt-2 space-y-1">
                  <li>All artifacts (skills, attributes, integrations)</li>
                  <li>All generated plans and LumaScript</li>
                  <li>All conversation history</li>
                  <li>All project stages and metadata</li>
                </ul>
              </div>

              <p className="text-sm text-muted-foreground">
                Are you sure you want to continue?
              </p>
            </div>
          ) : (
            // Stage 2: Type Project Name to Confirm
            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/50 rounded-md p-4">
                <p className="text-sm text-red-900 dark:text-red-100 font-medium">
                  ⚠️ Final Confirmation Required
                </p>
              </div>

              <div>
                <p className="text-sm text-foreground mb-3">
                  To confirm deletion, type the project name exactly:
                </p>
                <div className="bg-muted rounded-md p-3 mb-3">
                  <p className="text-sm font-mono font-medium text-center">
                    {project.name}
                  </p>
                </div>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type project name here"
                  className="w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  autoFocus
                  disabled={isDeleting}
                />
                {confirmText && !isConfirmTextValid && (
                  <p className="text-xs text-red-500 mt-2">
                    Project name does not match
                  </p>
                )}
              </div>

              <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/50 rounded-md p-3">
                <p className="text-xs text-orange-900 dark:text-orange-100">
                  This action will permanently delete <strong>{artifactCount} {artifactCount === 1 ? 'artifact' : 'artifacts'}</strong> and all associated data.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          {stage === 1 ? (
            <Button
              variant="outline"
              onClick={handleFirstConfirm}
              className="border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20"
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={handleFinalDelete}
              disabled={!isConfirmTextValid || isDeleting}
              className="border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                  Deleting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Trash2 className="w-4 h-4" />
                  Delete Project
                </span>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
